import type { Path } from 'history';

/**
 * Match paths in this set
 * /my/business/:id/tax
 * /my/account/:id/tax
 * /my/account/tax
 * See tests for further examples.
 */
export const isTaxPage = (location: Pick<Path, 'pathname'>) =>
  location.pathname.search(
    /^\/my\/(business|account)\/[^/]*(\/?)tax(\/.*)?$/g,
  ) > -1;

export const hasChanged = <T = unknown>(
  criteria: (a: T) => boolean,
  current?: T,
  next?: T,
) => (current && criteria(current)) !== (next && criteria(next));

/**
 * Replaces year placeholders ({{YEAR}}, {{PRIOR_YEAR}}) and literal year numbers with actual tax years
 * @param text - The text containing year placeholders or literal years (e.g., "2025, "2024")
 * @param taxYear - The tax year to replace with (defaults to current year - 1)
 * @returns The text with years replaced
 */
export const replaceTaxYear = (
  text: string | undefined | null,
  taxYear?: number | null,
): string => {
  if (!text) return '';

  const currentYear = taxYear ?? new Date().getFullYear() - 1;
  const priorYear = currentYear - 1;

  const has2025 = /\b2025\b/.test(text);

  const yearMap = has2025
    ? { '2025': currentYear, '2024': priorYear }
    : { '2024': currentYear, '2023': priorYear };

  return text
    .replace(/\b(2023|2024|2025)\b/g, (year) =>
      String(yearMap[year as keyof typeof yearMap] ?? year),
    )
    .replace(/\{\{YEAR\}\}/g, String(currentYear))
    .replace(/\{\{PRIOR_YEAR\}\}/g, String(priorYear));
};
